/* © 2017-2026 Booz Allen Hamilton Inc. All Rights Reserved. */

import get from 'lodash/get';
import sortBy from 'lodash/sortBy';
import { parseDate as parseDateBase, CalendarDate } from '@internationalized/date';
import { gnoment } from 'shared-ui/gnoment';

export function parseDate(date) {
    if (date instanceof CalendarDate) {
        return date;
    }
    return parseDateBase(gnoment(date).format('YYYY-MM-DD'));
}

export const dateTimeStringToDate = (dateTimeString) => {
    return dateTimeString.substring(0, 10);
};

// copied directly from index.js to avoid circular dependency
const isDaily = (passType) => {
    return passType?.duration?.endsWith('-day');
};

export function toAmPmTime(militaryTime) {
    let result = militaryTime;
    if (/^\d{4}$/.test(militaryTime)) {
        // If exactly 4 digits
        let hours = parseInt(militaryTime.substring(0, 2), 10);
        hours %= 24; //
        const amPm = hours < 12 ? ' AM' : ' PM';
        if (hours > 12) {
            hours -= 12;
        } else if (hours === 0) {
            hours = 12;
        }
        result = `${hours}:${militaryTime.substring(2)}${amPm}`;
    }
    return result;
}

function findPricingDivision(period, date) {
    if (!period || !date) return false;
    const day = date instanceof CalendarDate ? date : parseDate(date);
    const start = parseDate(period.start_date);
    if (start.compare(day) > 0) {
        return false;
    }

    for (const division of period.divisions) {
        if (!division) {
            return false;
        }
        const startsAt = parseDate(
            `${division.starts_at < division.ends_at ? day.year : day.year - 1}-${division.starts_at}`
        );
        const endsAt = parseDate(`${day.year}-${division.ends_at}`);

        const match =
            (startsAt.compare(day) <= 0 && endsAt.compare(day) >= 0) ||
            (startsAt.add({ years: 1 }).compare(day) <= 0 &&
                endsAt.add({ years: 1 }).compare(day) >= 0);
        if (match) {
            return division;
        }
    }

    return false;
}

// get active period
export function findPricingPeriodV2(periods, date) {
    if (!periods || !date) return false;
    const day = date instanceof CalendarDate ? date : parseDate(date);
    let period = false;
    let currentStart = null;
    for (const p of periods) {
        const startDate = parseDate(p.start_date);
        if (startDate.compare(day) <= 0 && currentStart == null) {
            period = p;
            currentStart = startDate;
        }
        if (
            startDate.compare(day) <= 0 &&
            currentStart !== null &&
            startDate.compare(currentStart) >= 0
        ) {
            period = p;
            currentStart = startDate;
        }
    }
    return period;
}

export function isFreeDay(facility, date) {
    if (!facility || !date) return false;
    const calendarDate = date instanceof CalendarDate ? date : parseDate(date);
    const freeDays = get(facility, 'free_days') || [];
    return !!freeDays.find((v) => {
        const freeDayDate = parseDate(v.date);
        return calendarDate.compare(freeDayDate) === 0;
    });
}

function hasClosure(passType, period, date) {
    if (!passType || !period || !date) return false;
    const calendarDate = date instanceof CalendarDate ? date : parseDate(date);
    const periodStartDate = parseDateBase(dateTimeStringToDate(period.start_date));
    const periodEndDate = parseDateBase(dateTimeStringToDate(period.end_date));
    const dateIsInPeriod =
        calendarDate.compare(periodStartDate) >= 0 &&
        calendarDate.compare(periodEndDate) <= 0;
    return (
        isDaily(passType) &&
        period?.pass_types?.some((id) =>
            ['ALL', passType?.id?.toUpperCase()].includes(id.toUpperCase())
        ) &&
        dateIsInPeriod
    );
}

export function isBlackoutDate(facility, passType, date) {
    const periods = get(facility, 'blackout_periods') || [];
    const result = periods.reduce(
        (acc, period) => acc || hasClosure(passType, period, date),
        false
    );
    return result;
}

export function getBlackoutPeriodForDate(facility, passType, date) {
    const periods = get(facility, 'blackout_periods') || [];
    for (const periodIndex in periods) {
        const period = periods[periodIndex];
        if (hasClosure(passType, period, date)) {
            return period;
        }
    }
    return null;
}

export function datesBlockedV2(facility, passType, checkClosures) {
    return (date) => {
        const pricingPeriod = findPricingPeriodV2(passType.periods, date);
        if (!pricingPeriod) {
            return 'blocked';
        }
        const pricingDivision = findPricingDivision(pricingPeriod, date);
        if (!pricingDivision) {
            return 'blocked';
        }

        const startsAt = parseDate(
            `${pricingDivision.starts_at < pricingDivision.ends_at ? date.year : date.year - 1}-${pricingDivision.starts_at}`
        );
        const endsAt = parseDate(`${date.year}-${pricingDivision.ends_at}`);
        if (date.compare(endsAt) > 0 && date.compare(startsAt) < 0) {
            return 'blocked';
        }

        if (checkClosures && isBlackoutDate(facility, passType, date)) {
            return 'closure';
        }

        if (checkClosures && isFreeDay(facility, date)) {
            return 'unavailableSelected';
        }

        return 'available';
    };
}

export function getDivisionFromPeriods(periods, date) {
    try {
        return findPricingDivision(findPricingPeriodV2(periods, date), date);
    } catch (e) {
        return {};
    }
}

export function getValidPriceFromDivisionV2(periods, date) {
    try {
        const sortedPeriods = sortBy(periods, 'start_date');
        let division = findPricingDivision(
            findPricingPeriodV2(sortedPeriods, date),
            date
        );
        if (!division) {
            division = sortedPeriods[sortedPeriods.length - 1].divisions[0];
        }
        return division.display_price;
    } catch (e) {
        return {};
    }
}

export function expandBack(divisions, div, startMonthDay) {
    if (div.starts_at === '01-01' && div.ends_at !== '12-31') {
        for (const division of divisions) {
            if (
                division.ends_at === '12-31' &&
                division.starts_at !== '01-01' &&
                div.name === division.name &&
                div.price === division.price
            ) {
                return [division, true];
            }
        }
    }
    // support for seasons across years
    if (
        div.starts_at > div.ends_at &&
        startMonthDay !== undefined &&
        startMonthDay <= div.ends_at
    ) {
        return [div, true];
    }

    return [div, false];
}

export function expandForward(divisions, div, startMonthDay) {
    if (div.starts_at !== '01-01' && div.ends_at === '12-31') {
        for (const division of divisions) {
            if (
                division.starts_at === '01-01' &&
                division.ends_at !== '12-31' &&
                division.name === div.name &&
                division.price === div.price
            ) {
                return [division, true];
            }
        }
    }
    // support for seasons across years
    if (
        (div.starts_at > div.ends_at || startMonthDay > div.starts_at) &&
        startMonthDay !== undefined &&
        startMonthDay > div.ends_at
    ) {
        return [div, true];
    }

    return [div, false];
}

export const isDailyPassType = (passType = '') => passType?.includes('day');

const DEFAULT_BOOKING_WINDOW_MONTHS = 6;
const AGENCY_BOOKING_WINDOW_MONTHS = {
    BLM: 6,
    BOR: 6,
    FWS: 6,
    NPS: 1,
    USACE: 1,
    USFS: 1,
};

export function getBookingWindowMonths(agency) {
    return AGENCY_BOOKING_WINDOW_MONTHS[agency] || DEFAULT_BOOKING_WINDOW_MONTHS;
}

export function isValidDateFormat(dateString) {
    const regex = /^\d{4}-\d{2}-\d{2}$/;
    return dateString && regex.test(dateString.split('T')[0]);
}
